<template>
  <!-- Settlement Details Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-receipt me-2"></i>
            Settlement Batch Details
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else>
            <!-- Batch Summary -->
            <div class="row mb-4">
              <div class="col-lg-8">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="text-muted mb-3">Batch Information</h6>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Batch Number</label>
                        <h5 class="mb-0">{{ batchDetails.batch.batch_number }}</h5>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Status</label>
                        <div>
                          <span :class="getStatusBadge(batchDetails.batch.status)" class="fs-6">
                            {{ batchDetails.batch.status }}
                          </span>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Hospital</label>
                        <h6 class="mb-0">{{ batchDetails.batch.hospital_name }}</h6>
                        <small class="text-muted">{{ batchDetails.batch.license_number }}</small>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Location</label>
                        <h6 class="mb-0">{{ batchDetails.batch.region_name }}</h6>
                        <small class="text-muted">{{ batchDetails.batch.woreda_name }}</small>
                      </div>
                      <div class="col-md-6">
                        <label class="text-muted small">Period</label>
                        <h6 class="mb-0">{{ formatDate(batchDetails.batch.period_start) }} - {{ formatDate(batchDetails.batch.period_end) }}</h6>
                      </div>
                      <div class="col-md-6">
                        <label class="text-muted small">Created Date</label>
                        <h6 class="mb-0">{{ formatDate(batchDetails.batch.created_at) }}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-4">
                <div class="card bg-primary text-white">
                  <div class="card-body">
                    <h6 class="text-white-50 mb-3">Financial Summary</h6>
                    <div class="mb-3">
                      <label class="text-white-50 small">Total Claims</label>
                      <h4 class="mb-0 text-white">{{ batchDetails.batch.total_claims }}</h4>
                    </div>
                    <div class="mb-3">
                      <label class="text-white-50 small">Total Amount</label>
                      <h4 class="mb-0 text-white">ETB {{ formatAmount(batchDetails.batch.total_amount) }}</h4>
                    </div>
                    <div v-if="batchDetails.batch.settled_amount">
                      <label class="text-white-50 small">Settled Amount</label>
                      <h4 class="mb-0 text-white">ETB {{ formatAmount(batchDetails.batch.settled_amount) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Information (if paid) -->
            <div v-if="batchDetails.batch.status === 'paid'" class="alert alert-success mb-4">
              <h6 class="alert-heading"><i class="bx bx-check-circle me-2"></i>Payment Information</h6>
              <div class="row mt-3">
                <div class="col-md-3">
                  <strong>Settlement Date:</strong><br>
                  {{ formatDate(batchDetails.batch.settlement_date) }}
                </div>
                <div class="col-md-3">
                  <strong>Bank Reference:</strong><br>
                  {{ batchDetails.batch.bank_reference }}
                </div>
                <div class="col-md-3">
                  <strong>Settled Amount:</strong><br>
                  ETB {{ formatAmount(batchDetails.batch.settled_amount) }}
                </div>
                <div class="col-md-3">
                  <strong>Approved By:</strong><br>
                  {{ batchDetails.batch.approved_by }}
                </div>
              </div>
            </div>

            <!-- Claims Table -->
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Claims in this Batch ({{ batchDetails.claimCount }})</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th>Claim Number</th>
                      <th>Member</th>
                      <th>Service Date</th>
                      <th>Type</th>
                      <th class="text-end">Approved Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="claim in batchDetails.claims" :key="claim.claim_id">
                      <td>{{ claim.claim_number }}</td>
                      <td>
                        <div>{{ claim.first_name }} {{ claim.surname }}</div>
                        <small class="text-muted">{{ claim.cbhi_number }}</small>
                      </td>
                      <td>{{ formatDate(claim.service_date) }}</td>
                      <td>{{ claim.type_name }}</td>
                      <td class="text-end fw-bold">ETB {{ formatAmount(claim.approved_amount) }}</td>
                    </tr>
                    </tbody>
                    <tfoot class="table-light">
                    <tr>
                      <td colspan="4" class="text-end fw-bold">Total:</td>
                      <td class="text-end fw-bold">ETB {{ formatAmount(batchDetails.batch.total_amount) }}</td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <!-- Payment History -->
            <div v-if="batchDetails.paymentHistory && batchDetails.paymentHistory.length > 0" class="card mt-4">
              <div class="card-header">
                <h6 class="mb-0">Payment History</h6>
              </div>
              <div class="card-body">
                <div class="timeline">
                  <div v-for="payment in batchDetails.paymentHistory" :key="payment.history_id" class="timeline-item">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                      <h6 class="mb-1">{{ payment.action }}</h6>
                      <p class="text-muted mb-0">{{ payment.notes }}</p>
                      <small class="text-muted">{{ formatDateTime(payment.created_at) }} by {{ payment.created_by }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Close
          </button>
          <button v-if="batch.status === 'pending'" @click="processBatch" class="btn btn-success">
            <i class="bx bx-check-circle me-1"></i> Process Batch
          </button>
          <button v-if="batch.status === 'processed'" @click="showPaymentForm = true" class="btn btn-primary">
            <i class="bx bx-money me-1"></i> Record Payment
          </button>
        </div>
      </div>
    </div>

    <!-- Payment Form Dialog -->
    <div v-if="showPaymentForm" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.7)">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Record Payment</h5>
            <button type="button" class="btn-close" @click="showPaymentForm = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Bank Reference <span class="text-danger">*</span></label>
              <input v-model="paymentDetails.bankReference" type="text" class="form-control"
                     placeholder="Enter bank reference number">
            </div>
            <div class="mb-3">
              <label class="form-label">Settled Amount <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">ETB</span>
                <input v-model="paymentDetails.settledAmount" type="number" class="form-control"
                       step="0.01" :placeholder="formatAmount(batch.total_amount)">
              </div>
              <small class="text-muted">Total batch amount: ETB {{ formatAmount(batch.total_amount) }}</small>
            </div>
            <div class="mb-3">
              <label class="form-label">Payment Date</label>
              <input v-model="paymentDetails.paymentDate" type="date" class="form-control">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showPaymentForm = false">Cancel</button>
            <button @click="submitPayment" class="btn btn-primary" :disabled="!canSubmitPayment">
              <i class="bx bx-check me-1"></i> Confirm Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "SettlementDetailsModal",
  props: {
    batch: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      batchDetails: {
        batch: {},
        claims: [],
        claimCount: 0,
        paymentHistory: []
      },
      showPaymentForm: false,
      paymentDetails: {
        bankReference: '',
        settledAmount: '',
        paymentDate: new Date().toISOString().split('T')[0]
      }
    };
  },

  computed: {
    show() {
      return this.batch != null;
    },

    canSubmitPayment() {
      return this.paymentDetails.bankReference &&
          this.paymentDetails.settledAmount > 0;
    }
  },

  watch: {
    batch(newVal) {
      if (newVal) {
        this.loadBatchDetails();
      }
    }
  },

  mounted() {
    if (this.batch) {
      this.loadBatchDetails();
    }
  },

  methods: {
    async loadBatchDetails() {
      this.loading = true;

      const data = {
        "function": "getSettlementBatchDetails",
        "batchId": this.batch.batch_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.batchDetails = res.data.data;
          // Set default payment amount
          this.paymentDetails.settledAmount = this.batchDetails.batch.total_amount;
        }
      } catch (e) {
        console.error("Failed to load batch details:", e);
      } finally {
        this.loading = false;
      }
    },

    processBatch() {
      this.$emit('process', this.batch);
    },

    async submitPayment() {
      if (!this.canSubmitPayment) return;

      this.$emit('pay', this.batch.batch_id, this.paymentDetails);
      this.showPaymentForm = false;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    getStatusBadge(status) {
      const badges = {
        'pending': 'badge bg-warning',
        'processed': 'badge bg-info',
        'paid': 'badge bg-success',
        'cancelled': 'badge bg-danger'
      };
      return badges[status] || 'badge bg-secondary';
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  padding-bottom: 20px;
}

.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: -21px;
  top: 20px;
  height: calc(100% - 10px);
  width: 2px;
  background-color: #e9ecef;
}

.timeline-marker {
  position: absolute;
  left: -26px;
  top: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #556ee6;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #e9ecef;
}
</style>